import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Box, Grid, Paper, Theme, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { PostingTemplate, ScreenEnum, ViewEnum } from '@professocial/professocialTypes/types/commonTypes'

import { getTheme } from '../../../store/common/common.selectors'
import SocialMediaTabs from '../NavigationComponents/SocialMediaTabs'
import InstaDots from '../../icons/InstaDots'
import { NetworkType } from '@3m5/sm_publish-frontend-api'
import ProfessocialPost from '../../../screens/ProfessocialPost'

interface IPostingPreviewProps {
  posting: PostingTemplate
  activeItem: NetworkType
  networkPageId: string
  onChangeTab?: (key: NetworkType) => void
  user: any
}

const PostingPreview: React.FC<IPostingPreviewProps> = (props) => {
  const isInstagram = props.activeItem === NetworkType.Instagram

  const useStyles = makeStyles((theme: Theme) => ({
    rootDesktop: {
      color: theme.palette.primary.main,
      cursor: 'pointer',
      paddingBottom: '10px',
    },
    paperDesktop: {
      position: 'relative',
      width: '100%',
      padding: 0,
      marginTop: '15px',
      '&::before': {
        content: 'url(/src/shared/assets/images/dots.svg)',
        display: 'block',
        position: 'absolute',
        color: theme.palette.secondary.main,
        height: '25px',
        width: '25px',
        top: '20px',
        right: '15px',
      },
    },
    rootMobile: {
      color: theme.palette.primary.main,
      cursor: 'pointer',
      paddingBottom: '10px',
    },
    boxBorder: {
      marginTop: '15px',
      padding: '2px',
      border: '.334vh solid #FBFBFB',
      borderRadius: '3.6vh',
      width: '342px',
    },
    boxContent: {
      display: 'flex',
      position: 'absolute',
      alignItems: 'baseline',
      backgroundColor: '#C9CDD0',
      height: 'calc(100% - 90px)',
      overflowY: 'scroll',
      top: isInstagram ? 55 : 42,
      left: 1,
      right: -10,
    },
    boxWrapper: {
      position: 'relative',
      width: '100%',
      '&::before': {
        content: !isInstagram ? 'url(/src/shared/assets/images/dots.svg)' : '',
        display: 'block',
        position: 'absolute',
        color: theme.palette.secondary.main,
        height: '25px',
        width: '25px',
        top: '20px',
        right: '15px',
      },
    },
    istagramDots: {
      position: 'absolute',
      top: '9px',
      right: '6px',
      transform: 'rotate(90deg)',
    },
    paperMobile: {
      position: 'relative',
      overflow: 'hidden',
      width: '100%',
      height: '650px',
      padding: '1px',
      border: '1.334vh solid #FBFBFB',
      borderRadius: '3.3vh',
      boxShadow: 'inset 3px 3px 1px -1px rgb(0 0 0 / 20%), inset 0px 1px 1px 1px rgb(0 0 0 / 14%), 0px 1px 8px 0px rgb(0 0 0 / 12%)',
      '&::after': {
        content: isInstagram ? 'url(/src/shared/assets/images/instagram-footer.png)' : 'url(/src/shared/assets/images/footer.png)',
        display: 'block',
        position: 'absolute',
        height: '50px',
        width: '304px',
        bottom: isInstagram ? '1px' : '0px',
        right: '0px',
        left: '0px',
        padding: '1px',
      },
    },
    img: {
      borderRadius: 2,
      width: 'auto',
      display: 'inline',
      verticalAlign: 'bottom',
      maxWidth: '100%',
    },
  }))

  const { t } = useTranslation()
  const classes = useStyles()

  const theme = useSelector(getTheme)

  const [posting, setPosting] = useState<PostingTemplate>()
  const [tabs, setTabs] = React.useState<NetworkType[]>([NetworkType.Facebook, NetworkType.Instagram, NetworkType.LinkedIn])

  useEffect(() => {
    setPosting(props.posting)
    const overviewTabs: NetworkType[] = []
    if (props.posting && props.posting.socialMediaNetwork.facebook) {
      overviewTabs.push(NetworkType.Facebook)
    }
    if (props.posting && props.posting.socialMediaNetwork.instagram) {
      overviewTabs.push(NetworkType.Instagram)
    }
    if (props.posting && props.posting.socialMediaNetwork.linkedIn) {
      overviewTabs.push(NetworkType.LinkedIn)
    }
    setTabs(overviewTabs)
  }, [props.posting],
  )

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <SocialMediaTabs activeItem={props.activeItem} tabs={tabs} onChangeTab={(tab:NetworkType) => { props.onChangeTab && props.onChangeTab(tab) }} />
        </Grid>
        {props.activeItem !== NetworkType.Instagram &&
          <Grid item xs={12} lg={6}>
            <Box pr={8}>
              <Typography variant='body1' sx={{ mt: 4 }}>{t('postingCommon:desktopPreview')}:</Typography>
              <Paper id='desktop-preview' elevation={3} className={classes.paperDesktop}>
                <Grid container>
                  <ProfessocialPost
                    postConfiguration={{
                      theme: theme,
                      userRole: props.user.role,
                      size: ScreenEnum.xl,
                      view: ViewEnum.preview,
                      networkType: props.activeItem,
                      posting: posting,
                      networkPageId: props.networkPageId,
                    }}
                  />
                </Grid>
              </Paper>
            </Box>
          </Grid>}
        <Grid item xs={12} lg={6}>
          <Typography variant='body1' sx={{ mt: 4 }}>{t('postingCommon:mobilePreview')}:</Typography>
          <Paper id='mobile-preview' elevation={3} className={classes.boxBorder}>
            <Box className={classes.paperMobile}>
              {isInstagram ? <img className={classes.img} src='/src/shared/assets/images/instagram-head.png' alt='' /> : <img className={classes.img} src='/src/shared/assets/images/header.png' />}
              <Box className={classes.boxContent}>
                <Box className={classes.boxWrapper}>
                  {isInstagram && <InstaDots className={classes.istagramDots} />}
                  <Grid container style={{ backgroundColor: '#fff' }}>
                    <ProfessocialPost
                      postConfiguration={{
                        theme: theme,
                        userRole: props.user.role,
                        size: ScreenEnum.xs,
                        view: ViewEnum.preview,
                        networkType: props.activeItem,
                        posting: posting,
                        networkPageId: props.networkPageId,
                      }}
                    />
                  </Grid>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  )
}

export default PostingPreview
